import { zodResolver } from '@hookform/resolvers/zod';
import { nonEmptyArray } from '@utils/zodHelpers';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import type { UserResponse } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';
import z from 'zod';
import { ErrorMessage } from '~/components/form-fields/ErrorMessage';
import { TextInput } from '~/components/form-fields/native-html/TextInput';
import { RequestButton } from '~/components/utils/buttons';
import { useStreamChatContext } from '~/react_context/StreamChatContext';
import StreamChatUserSelectList from './StreamChatUserSelectList';

const formSchema = z.object({
	channelName: z
		.string()
		.min(2, 'Channel name must be at least 2 characters')
		.regex(/^[\w!\-_]+$/, 'Channel name can only contain letters, numbers or "!-_"'),
	members: nonEmptyArray(
		z.object({
			id: z.string(),
			image: z.string().optional(),
			name: z.string().optional(),
		})
	),
});
type FormType = z.infer<typeof formSchema>;

export default function CreateChannelForm() {
	const { createChannel, closeChatOverlay } = useStreamChatContext();
	const { setActiveChannel } = useChatContext();
	const [isCreating, setIsCreating] = useState(false);
	const {
		control,
		formState: { errors },
		handleSubmit,
		register,
	} = useForm<FormType>({
		resolver: zodResolver(formSchema),
	});
	const {
		append: addMember,
		fields: selectedMembers,
		remove: removeMember,
	} = useFieldArray({
		control,
		name: 'members',
	});

	const onUserDeselected = (user: UserResponse) => removeMember(selectedMembers.findIndex((member) => member.id === user.id));
	const onUserSelected = (user: UserResponse) => addMember({ id: user.id, image: user.image, name: user.name });

	const onSubmit = handleSubmit(async (data) => {
		setIsCreating(true);

		const channel = await createChannel({
			channelType: 'team',
			members: data.members.map((member) => member.id),
			name: data.channelName,
		});

		setActiveChannel(channel);
		setIsCreating(false);
		closeChatOverlay();
	}, console.warn);

	return (
		<form className="h-full" onSubmit={onSubmit}>
			<h1 className="text-lg mb-4">Create Channel</h1>
			<div className="overflow-y-auto h-[calc(100%-100px)]">
				<TextInput
					errorMessage={errors?.channelName?.message}
					id="channelName"
					labelText="Channel Name"
					name="channelName"
					placeholder="channel-name (no spaces)"
					register={register}
				/>
				<div className="my-4">
					<StreamChatUserSelectList onUserDeselected={onUserDeselected} onUserSelected={onUserSelected} />
				</div>
				<ErrorMessage errorMessage={errors?.members?.message} />
			</div>
			<div className="my-4 flex items-center justify-between">
				<button
					type="button"
					className="rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-lg"
					onClick={closeChatOverlay}
				>
					Cancel
				</button>
				<RequestButton
					className="rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-xs hover:bg-primary-dark focus:outline-hidden focus:ring-2 focus:bg-primary focus:ring-offset-2 sm:col-start-2 sm:text-lg disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
					isLoading={isCreating}
				>
					Create Channel
				</RequestButton>
			</div>
		</form>
	);
}
